/**
 * Performance Claims Module
 * 
 * Provides conservative, range-based performance claims with proper citations
 * to ensure legal safety and engineer respectability.
 */

export interface PerformanceClaimRange {
  min: number;
  max: number;
  unit: string;
  footnote?: string;
}

/**
 * Conditional SCOP range showing both current and upgraded scenarios
 */
export interface ConditionalScopClaim {
  /** Current emitter configuration SCOP */
  asIs: PerformanceClaimRange;
  /** With emitter upgrades SCOP */
  withUpgrades: PerformanceClaimRange;
  /** Estimated flow temperature with current emitters */
  currentFlowTempC: number;
  /** Design flow temperature with upgrades */
  upgradedFlowTempC: number;
  /** Whether low-temp emitters are confirmed */
  lowTempConfirmed: boolean;
}

export interface PerformanceClaims {
  // Gas consumption reduction vs baseline
  gasReduction?: PerformanceClaimRange;
  
  // Heat pump seasonal coefficient of performance
  scop?: PerformanceClaimRange;
  
  // Conditional SCOP (for heat pumps when emitters not confirmed)
  scopConditional?: ConditionalScopClaim;
  
  // System lifespan
  lifespan?: PerformanceClaimRange;
  
  // Standing loss (cylinder)
  standingLoss?: PerformanceClaimRange;
  
  // Annual gas/electricity use
  annualGasUse?: PerformanceClaimRange;
  annualElectricityUse?: PerformanceClaimRange;
}

/**
 * Standard footnotes for performance claims
 */
export const PERFORMANCE_FOOTNOTES = {
  gasReduction: 'Based on manufacturer data and BEAMA-supported control efficiency studies.',
  scop: 'Actual performance depends on emitter sizing and flow temperature requirements.',
  lifespan: 'Typical lifespan depending on maintenance and usage profile.',
  standingLoss: 'Compared to typical modern cylinders.',
  mixergyReduction: 'Smart stratification can reduce gas use depending on draw pattern and control strategy.',
} as const;

/**
 * Generate conservative gas reduction claim for smart controls/cylinders
 */
export function getGasReductionClaim(systemType: string, cylinderType?: string): PerformanceClaimRange | undefined {
  // Mixergy/smart cylinders: 10-20% reduction
  if (cylinderType?.toLowerCase().includes('mixergy')) {
    return {
      min: 10,
      max: 20,
      unit: '%',
      footnote: PERFORMANCE_FOOTNOTES.mixergyReduction + ' ' + PERFORMANCE_FOOTNOTES.gasReduction,
    };
  }
  
  return undefined;
}

/**
 * Check if low-temperature emitters are confirmed
 * Returns true only when we have explicit confirmation of low-temp capability
 */
export function isLowTempConfirmed(emitterSuitability?: string, emitterType?: string): boolean {
  // Underfloor heating is always low-temp compatible
  if (emitterType === 'underfloor' || emitterType === 'mixed') {
    return true;
  }
  
  // Explicitly confirmed as low-temp suitable
  if (emitterSuitability === 'likely_ok') {
    return true;
  }
  
  // Unknown or needs upgrade = not confirmed
  return false;
}

/**
 * Get SCOP range for "as-is" scenario (current emitters, likely higher flow temps)
 * 
 * @param flowTemp - Estimated flow temperature in °C (default: 55°C for typical UK retrofits)
 * @returns Performance claim range with appropriate SCOP values
 * 
 * Note: Boundary conditions are intentional:
 * - flowTemp >= 55: Conservative range for standard radiators (2.8-3.4)
 * - flowTemp >= 45: Mid-range for borderline systems (3.0-3.6)
 * - flowTemp < 45: Better range for already low-temp systems (3.5-4.2)
 */
export function getScopAsIs(flowTemp: number = 55): PerformanceClaimRange {
  // Conservative estimate for existing radiators at typical flow temps
  if (flowTemp >= 55) {
    return {
      min: 2.8,
      max: 3.4,
      unit: '',
      footnote: 'Based on 50–55°C flow temperature with current emitters. Actual performance varies with installation quality and usage patterns.',
    };
  } else if (flowTemp >= 45) {
    return {
      min: 3.0,
      max: 3.6,
      unit: '',
      footnote: 'Based on 45–50°C flow temperature. Actual performance varies with installation quality and usage patterns.',
    };
  } else {
    // Already low-temp
    return {
      min: 3.5,
      max: 4.2,
      unit: '',
      footnote: 'Based on low-temperature operation (35–45°C). Actual performance varies with installation quality and usage patterns.',
    };
  }
}

/**
 * Get SCOP range for "with upgrades" scenario (low-temp emitters confirmed)
 */
export function getScopWithUpgrades(): PerformanceClaimRange {
  return {
    min: 4.0,
    max: 4.5,
    unit: '',
    footnote: 'Based on low-temperature operation (35–45°C flow) with properly sized emitters. Actual performance varies with installation quality and usage patterns.',
  };
}

/**
 * Generate SCOP claim for heat pump systems
 * 
 * Returns appropriate SCOP range based on emitter configuration:
 * - If low-temp emitters confirmed: show optimistic range (4.0-4.5)
 * - Otherwise: show conservative range for current configuration
 */
export function getScopClaim(
  flowTemp: number, 
  systemType: string,
  emitterSuitability?: string,
  emitterType?: string
): PerformanceClaimRange | undefined {
  if (systemType !== 'heat_pump') {
    return undefined;
  }
  
  // Check if low-temp emitters are confirmed
  const lowTempConfirmed = isLowTempConfirmed(emitterSuitability, emitterType);
  
  if (lowTempConfirmed) {
    // Low-temp confirmed: show optimistic range
    return getScopWithUpgrades();
  }
  
  // Not confirmed: show range based on current/expected flow temp
  return getScopAsIs(flowTemp);
}

/**
 * Generate lifespan claim based on system type and brand quality
 */
export function getLifespanClaim(systemType: string, brandDesignLife?: string): PerformanceClaimRange {
  // Heat pumps typically have shorter lifespan
  if (systemType === 'heat_pump') {
    return {
      min: 15,
      max: 20,
      unit: 'years',
      footnote: PERFORMANCE_FOOTNOTES.lifespan,
    };
  }
  
  // Adjust based on brand design life
  if (brandDesignLife === 'LONG') {
    return {
      min: 18,
      max: 25,
      unit: 'years',
      footnote: PERFORMANCE_FOOTNOTES.lifespan,
    };
  } else if (brandDesignLife === 'SHORT') {
    return {
      min: 12,
      max: 18,
      unit: 'years',
      footnote: PERFORMANCE_FOOTNOTES.lifespan,
    };
  }
  
  // Standard claim
  return {
    min: 15,
    max: 25,
    unit: 'years',
    footnote: PERFORMANCE_FOOTNOTES.lifespan,
  };
}

/**
 * Generate standing loss claim for cylinders
 */
export function getStandingLossClaim(cylinderType: string, cylinderSize?: number): PerformanceClaimRange | undefined {
  const lowerCyl = cylinderType.toLowerCase();
  
  // Mixergy cylinders - very low standing loss
  if (lowerCyl.includes('mixergy')) {
    return {
      min: 0.7,
      max: 1.2,
      unit: 'kWh/day',
      footnote: 'Usage dependent. ' + PERFORMANCE_FOOTNOTES.standingLoss,
    };
  }
  
  // Modern unvented cylinders
  if (lowerCyl.includes('unvented') || lowerCyl.includes('uv')) {
    return {
      min: 1.0,
      max: 1.6,
      unit: 'kWh/day',
      footnote: PERFORMANCE_FOOTNOTES.standingLoss,
    };
  }
  
  // Standard vented cylinders
  if (lowerCyl.includes('vented') || lowerCyl.includes('ov')) {
    return {
      min: 1.2,
      max: 1.8,
      unit: 'kWh/day',
      footnote: PERFORMANCE_FOOTNOTES.standingLoss,
    };
  }
  
  return undefined;
}

/**
 * Calculate standing loss reduction percentage vs standard cylinder
 */
export function getStandingLossReduction(cylinderType: string): PerformanceClaimRange | undefined {
  const lowerCyl = cylinderType.toLowerCase();
  
  // Mixergy vs standard modern cylinder
  if (lowerCyl.includes('mixergy')) {
    return {
      min: 30,
      max: 60,
      unit: '%',
      footnote: PERFORMANCE_FOOTNOTES.standingLoss,
    };
  }
  
  return undefined;
}

/**
 * Generate conditional SCOP claim showing both current and upgraded scenarios
 * This is the recommended approach when emitter suitability is unknown
 */
export function getConditionalScopClaim(
  flowTemp: number = 55,
  systemType: string = 'heat_pump',
  emitterSuitability?: string,
  emitterType?: string
): ConditionalScopClaim | undefined {
  if (systemType !== 'heat_pump') {
    return undefined;
  }
  
  const lowTempConfirmed = isLowTempConfirmed(emitterSuitability, emitterType);
  
  return {
    asIs: getScopAsIs(flowTemp),
    withUpgrades: getScopWithUpgrades(),
    currentFlowTempC: flowTemp,
    upgradedFlowTempC: 40, // Target low-temp design
    lowTempConfirmed,
  };
}

/**
 * Format range for display
 */
export function formatPerformanceRange(range: PerformanceClaimRange): string {
  if (range.min === range.max) {
    return `${range.min}${range.unit}`;
  }
  
  if (range.unit === '%') {
    return `${range.min}–${range.max}${range.unit}`;
  }
  
  return `${range.min}–${range.max} ${range.unit}`;
}
